import { defineModel } from "../define-model";

export type ProjectBrand = {
	voice?: string;
	audience?: string;
	keywords?: string[];
	links?: { label: string; url: string }[];
	avoid?: string[];
};

export type MonitoringCadence = "hourly" | "every_6h" | "every_12h" | "daily" | "weekly";

/** Mirrors `api/src/types/projects.ts`; stored rows may be partial — read via `resolveMonitoring`. */
export type MonitoringSettings = {
	enabled: boolean;
	cadence: MonitoringCadence;
	/** 0–23, project timezone (daily / weekly, and the offset of 6h / 12h). */
	hour: number;
	/** 0 = Sunday (weekly). */
	weekday: number;
	tiers: {
		hotHours: number;
		warmDays: number;
		coldDays: number;
		archive: "weekly" | "monthly" | "off";
	};
	comments: { enabled: boolean; maxPagesPerPost: number };
	followers: boolean;
};

export type AgentSettings = {
	/** Autonomy of new agents and of flow runs no agent owns. */
	defaultAutonomy: "suggest" | "approve" | "auto";
	/** Project-wide daily cap (millicredits) across agents and the flows they start; null = none. */
	dailyBudgetMc: number | null;
	/** Kill switch: no agent in the project runs. */
	paused: boolean;
};

export type ProjectSettings = {
	monitoring?: Partial<MonitoringSettings>;
	agents?: Partial<AgentSettings>;
};

export const DEFAULT_AGENT_SETTINGS: AgentSettings = {
	defaultAutonomy: "approve",
	dailyBudgetMc: null,
	paused: false,
};

export const resolveAgentSettings = (settings: ProjectSettings | undefined): AgentSettings => ({
	...DEFAULT_AGENT_SETTINGS,
	...settings?.agents,
});

export const DEFAULT_MONITORING: MonitoringSettings = {
	enabled: true,
	cadence: "hourly",
	hour: 9,
	weekday: 1,
	tiers: { hotHours: 48, warmDays: 30, coldDays: 90, archive: "monthly" },
	comments: { enabled: true, maxPagesPerPost: 5 },
	followers: true,
};

export const resolveMonitoring = (settings: ProjectSettings | undefined): MonitoringSettings => {
	const m = settings?.monitoring ?? {};
	return {
		...DEFAULT_MONITORING,
		...m,
		tiers: { ...DEFAULT_MONITORING.tiers, ...m.tiers },
		comments: { ...DEFAULT_MONITORING.comments, ...m.comments },
	};
};

export type Project = {
	id: string;
	workspaceId: string;
	name: string;
	description: string;
	color: string;
	icon: string;
	timezone: string;
	brand: ProjectBrand;
	settings: ProjectSettings;
	sortOrder: number;
	createdById: string | null;
	archivedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

declare module "../types" {
	interface SyncModels {
		project: Project;
	}
}

export const model = defineModel({
	name: "project",
	load: "eager",
	defaults: ({ userId, workspaceId, now }) => ({
		workspaceId,
		description: "",
		color: "neutral",
		icon: "grid",
		timezone: "UTC",
		brand: {},
		settings: {},
		sortOrder: 0,
		createdById: userId,
		archivedAt: null,
		createdAt: now,
		updatedAt: now,
	}),
	sort: (a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt),
});
